import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Mappable } from '@atlaskit/editor-prosemirror/transform';
import { ReplaceStep, Step, StepMap, StepResult } from '@atlaskit/editor-prosemirror/transform';

export const stepType = 'editor-linking-meta';
export const invertStepType = 'editor-linking-meta-invert';

export type LinkStepMetadata = {
	/**
	 * Editor action performed
	 */
	action?: string;
	/**
	 * The applicable card action for this step
	 * if is RESOLVE then the undo/redo steps should be considered "updates" of a link
	 * if is AUTO_CONVERT then the transaction is converting a link to a non-link node (e.g. extension)
	 * and the link removal should not be tracked as a deletion
	 */
	cardAction?: 'RESOLVE' | 'AUTO_CONVERT';
	/**
	 * Editor input method that triggered the transaction
	 */
	inputMethod?: string;
	/**
	 * Source UI that tiggered this step, if available/applicable
	 */
	sourceEvent?: unknown;
};

/**
 * Custom Prosemirror Step to attach metadata about user interactions with links
 * Using a Step means that it will work with prosemirror-history and we get utilise when
 * firing events on history change
 */
export class LinkMetaStep extends Step {
	constructor(
		private pos: number | null,
		private metadata: LinkStepMetadata,
		private isInverted: boolean = false,
	) {
		super();
	}

	public getMetadata(): LinkStepMetadata {
		return this.metadata;
	}

	/**
	 * Generate new undo/redo analytics event when step is inverted
	 */
	invert(): LinkMetaStep {
		/**
		 * Omit sourceEvent in history
		 */
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { sourceEvent, ...metadata } = this.metadata;
		return new LinkMetaStep(this.pos, metadata, true);
	}

	// Should make no modifications to the doc
	apply(doc: PMNode): StepResult {
		return StepResult.ok(doc);
	}

	map(mapping: Mappable): LinkMetaStep {
		let newPos = this.pos;
		if (typeof newPos === 'number') {
			newPos = mapping.map(newPos);
		}
		// Return the same events, this step will never be removed
		return new LinkMetaStep(newPos, this.metadata, this.isInverted);
	}
	getMap(): StepMap {
		return new StepMap([this.pos || 0, 0, 0]);
	}

	// Return null to avoid merging events
	merge(): null {
		return null;
	}

	toJSON() {
		// When serialized we should create a noop Replace step
		return {
			stepType: 'replace',
			from: 0,
			to: 0,
		};
	}

	static fromJSON(): ReplaceStep {
		// This is a "local custom step" once serialized
		// we need to transform it in a no-operation action
		return new ReplaceStep(0, 0, Slice.empty);
	}
}

/** Register this step with Prosemirror */
Step.jsonID(stepType, LinkMetaStep);
